/**
 * labels — per-tenant vocabulary overrides (LABEL-OVERRIDES-EXPEDIENT, 2026-07-14
 * docket §1). The CD-owned half of the surgical noun override (e.g. a university
 * tenant renaming "Customer" → "Student"): a tiny resolver the ~8 genuine
 * user-facing "Customer" literals read through, fed by a per-tenant vocabulary
 * map on the existing config paths. The platform half ships the config key +
 * plumb-through; until a map arrives every label renders its fallback verbatim,
 * so this is a zero-behavior-change seam.
 *
 * Sources (both wired, first non-empty wins per key):
 *   • window.__STUDIO_LABELS__ — injected by studio-shell at deploy, same
 *     pattern as __STUDIO_API_BASE__. Read once at module init.
 *   • configureTenantLabels(map) — called from SettingsGeneral's
 *     getGeneralSettings hydrate when the settings payload carries a
 *     `labels` object (the declared config key; see changelog).
 *
 * Usage in a component:  const label = useTenantLabels(); label("customer", "Customer")
 * (the hook subscribes the caller so a late-arriving map re-renders it live —
 * same reactive pattern as lib/datetime). Outside render, the plain `label()`
 * export resolves against the current map without subscribing.
 *
 * Keys are lower-camel nouns ("customer", "customers"); values are the tenant's
 * display words. Mid-sentence usages lowercase the resolved value themselves.
 */
import { useSyncExternalStore } from "react";
import { onTenantChange } from "../shell/access";

function _readGlobal(): Record<string, string> {
  try {
    const w = window as { __STUDIO_LABELS__?: unknown };
    const m = w.__STUDIO_LABELS__;
    return m && typeof m === "object" && !Array.isArray(m) ? (m as Record<string, string>) : {};
  } catch {
    return {};
  }
}

let _labels: Record<string, string> = _readGlobal();
let _version = 0;
const _listeners = new Set<() => void>();
// Tenant-vocabulary reset (docket 2026-07-26): a switch must not carry tenant
// A's noun overrides into tenant B's session; SettingsGeneral re-populates for the new tenant on its own next hydrate.
onTenantChange(() => { _labels = _readGlobal(); _version += 1; _listeners.forEach((l) => { try { l(); } catch { /* noop */ } }); });

/** Merge a tenant vocabulary map in (e.g. from the general-settings payload).
 *  Unknown/empty input is ignored; no-op merges don't notify. */
export function configureTenantLabels(map: Record<string, string> | null | undefined): void {
  if (!map || typeof map !== "object" || Array.isArray(map)) return;
  const next = { ..._labels };
  let changed = false;
  for (const [k, v] of Object.entries(map)) {
    if (typeof v === "string" && v.trim() && next[k] !== v) { next[k] = v; changed = true; }
  }
  if (!changed) return;
  _labels = next;
  _version += 1;
  _listeners.forEach((l) => { try { l(); } catch { /* noop */ } });
}

/** Resolve one vocabulary key; the fallback is the canonical product word. */
export function label(key: string, fallback: string): string {
  const v = _labels[key];
  return typeof v === "string" && v.trim() ? v : fallback;
}

export function subscribeTenantLabels(fn: () => void): () => void {
  _listeners.add(fn);
  return () => { _listeners.delete(fn); };
}

/** Subscribe the calling component to vocabulary changes and get the resolver. */
export function useTenantLabels(): (key: string, fallback: string) => string {
  useSyncExternalStore(subscribeTenantLabels, () => _version, () => _version);
  return label;
}
